import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import NoAccessPage from "./NoAccessPage";
import IsLoading from "../components/IsLoading";
import PaginationButtonToolbar from "../components/PaginatedButtonToolbar";
import { formatDateAndTimeOnline } from "../components/DateAndTimeFunctions";
import CustomDateInputs from "../components/CustomDateInputs";
import "react-datepicker/dist/react-datepicker.css";
import useJWTInterceptors from "../authentication/useJWTInterceptors";
import { useParams } from "react-router-dom";

const TestStationFullDataPaginated = () => {
  const { theRouteUrl } = useParams();
  const isCompany = useSelector((state) => state.login.isCompany);
  const baseURL = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(true);
  const [device, setDevice] = useState("SATA2");
  const [deviceID, setDeviceID] = useState(theRouteUrl);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [count, setCount] = useState(0);
  const [pageIsValid, setPageIsValid] = useState(true);
  const [totalPageNumber, setTotalPageNumber] = useState(0);
  const [currentPageData, setCurrentPageData] = useState([]);

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const [selectedStartDateAndTime, setSelectedStartDateAndTime] =
    useState(yesterday);
  const [selectedEndDateAndTime, setSelectedEndDateAndTime] = useState(
    new Date()
  );

  const apiUrl = `${baseURL}test/${device.toUpperCase()}/${deviceID}?start_date=${formatDateAndTimeOnline(
    selectedStartDateAndTime
  )}&end_date=${formatDateAndTimeOnline(
    selectedEndDateAndTime
  )}&page=${pageNumber}`;

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await useJWTInterceptors.get(apiUrl);
      setCurrentPageData(response.data.results);
      setTotalPageNumber(Math.ceil((response.data.count || 0) / 50));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageNumber, count]);

  const downloadExcelReport = async () => {
    setExcelLoading(true);

    const excelApiUrl = `${baseURL}${device}_excel/?device_id=${deviceID}&start_date=${selectedStartDateAndTime}&end_date=${selectedEndDateAndTime}&device_type=${
      device === "SAPA" ? 1 : 0
    }&granularity=0`;

    try {
      // Make a GET request to the server to fetch the Excel file
      const response = await useJWTInterceptors.get(excelApiUrl, {
        responseType: "blob", // set responseType to 'blob' for binary data
      });

      // Create a blob from the binary data received in the response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      // Create a URL for the blob and use it to create a link element
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      // Set the link's properties for download
      link.href = url;
      link.download = `${formatDateAndTimeOnline(
        selectedStartDateAndTime
      )}_${formatDateAndTimeOnline(selectedEndDateAndTime)}.xlsx`; // specify the desired file name

      // Append the link to the document body and trigger a click to start download
      document.body.appendChild(link);
      link.click();

      // Clean up the created elements after the download is initiated
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error("Error downloading Excel report:", error);
      setExcelError(error);
      // Handle the error (e.g., show a user-friendly message)
    } finally {
      setExcelLoading(false);
    }
    // console.log("excelLoading:", excelLoading);
  };

  const convertToJalali = (x) => {
    const A = x.slice(0, 10);
    const B = x.slice(11, 19);
    const C = new Date(A);
    const D = new Intl.DateTimeFormat("fa-IR").format(C);
    const E = D.concat(" ", "(", B, ")");
    return E;
  };

  if (!isCompany) {
    return <NoAccessPage />;
  } else if (loading && isCompany) {
    return <IsLoading />;
  } else if (currentPageData && isCompany) {
    return (
      <div className="container-fluid p-2 text-medium">
        <div className="row">
          <CustomDateInputs
            type="test"
            startDateAndTime={selectedStartDateAndTime}
            endDateAndTime={selectedEndDateAndTime}
            setSelectedStartDateAndTime={setSelectedStartDateAndTime}
            setSelectedEndDateAndTime={setSelectedEndDateAndTime}
            setCount={setCount}
            count={count}
            excelLoading={excelLoading}
            downloadExcelReport={downloadExcelReport}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
          />
          <div className="col-lg-9 col-xl-10 " style={{ overflowX: "scroll" }}>
            <div dir="ltr" className="mb-1 d flex">
              {currentPageData.length > 0 ? (
                <PaginationButtonToolbar
                  setSelectedStartDateAndTime
                  pageNumber={pageNumber}
                  totalPageNumber={totalPageNumber}
                  setPageNumber={setPageNumber}
                />
              ) : (
                ""
              )}
            </div>
            {Array.isArray(currentPageData) && currentPageData.length > 0 ? (
              <div className="bg-secondary p-1">
                <table
                  id="sensorTable"
                  className="table bg-light table-striped table-bordered table-sm mx-auto text-center"
                  style={{ overflowX: "scroll", maxWidth: "screen" }}
                  cellSpacing="0"
                  dir="rtl"
                  width="90%"
                >
                  <thead>
                    <tr>
                      <th className="p-3">سرعت باد</th>
                      <th className="p-3">جهت باد</th>
                      <th className="p-3">باران </th>
                      <th className="p-3">تابش خورشید </th>
                      <th className="p-3">دما </th>
                      <th className="p-3">رطوبت</th>
                      <th className="p-3">دمای خاک </th>
                      <th className="p-3">رطوبت خاک </th>
                      <th className="p-3">برف</th>
                      <th className="p-3">تبخیر</th>
                      <th className="p-3">ساعت و زمان</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageData.map((station) => (
                      <tr>
                        {(station.wind_speed !== undefined &&
                          station.wind_speed !== null) ||
                        station.wind_speed === 0 ? (
                          <td>
                            {station.wind_speed} <sub>m/s</sub>
                          </td>
                        ) : (
                          <td>-</td>
                        )}

                        {(station.wind_direction !== undefined &&
                          station.wind_direction !== null) ||
                        station.wind_direction === 0 ? (
                          <td>
                            {station.wind_direction} <sub>°</sub>
                          </td>
                        ) : (
                          <td>-</td>
                        )}

                        {(station.rain !== undefined &&
                          station.rain !== null) ||
                        station.rain === 0 ? (
                          <td>
                            {station.rain} <sub>mm/min</sub>
                          </td>
                        ) : (
                          <td>-</td>
                        )}

                        {(station.solar_radiation !== undefined &&
                          station.solar_radiation !== null) ||
                        station.solar_radiation === 0 ? (
                          <td>
                            {station.solar_radiation} <sub>w/m²</sub>
                          </td>
                        ) : (
                          <td>-</td>
                        )}

                        {(station.temperature !== undefined &&
                          station.temperature !== null) ||
                        station.temperature === 0 ? (
                          <td>
                            {station.temperature} <sub>°C</sub>
                          </td>
                        ) : (
                          <td>-</td>
                        )}

                        {(station.humidity !== undefined &&
                          station.humidity !== null) ||
                        station.humidity === 0 ? (
                          <td>
                            {station.humidity} <sub>%RH</sub>
                          </td>
                        ) : (
                          <td>-</td>
                        )}

                        {(station.soil_temperature !== undefined &&
                          station.soil_temperature !== null) ||
                        station.soil_temperature === 0 ? (
                          <td>
                            {station.soil_temperature} <sub>°C</sub>
                          </td>
                        ) : (
                          <td>-</td>
                        )}

                        {(station.soil_moisture !== undefined &&
                          station.soil_moisture !== null) ||
                        station.soil_moisture === 0 ? (
                          <td>
                            {station.soil_moisture} <sub>%RH</sub>
                          </td>
                        ) : (
                          <td>-</td>
                        )}

                        {(station.snow !== undefined &&
                          station.snow !== null) ||
                        station.snow === 0 ? (
                          <td>
                            {station.snow} <sub>mm</sub>
                          </td>
                        ) : (
                          <td>-</td>
                        )}

                        {(station.evaporation !== undefined &&
                          station.evaporation !== null) ||
                        station.evaporation === 0 ? (
                          <td>
                            {station.evaporation} <sub>m/s</sub>
                          </td>
                        ) : (
                          <td>-</td>
                        )}

                        {station.ccreated_datetime ? (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.ccreated_datetime)}
                          </td>
                        ) : (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.created_datetime)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
};

export default TestStationFullDataPaginated;
